import { buildInitialQuizDataState } from "./quiz-results-data.js";
import { fetchBlurbText, renderBlurbBlock } from "./quiz-results-blurb.js";
import { renderConcernsBlock } from "./quiz-results-concerns.js";
import {
  renderRegimenPriceLabel,
  renderRegimenSliderSection,
} from "./quiz-results-regimen-slider.js";
import {
  bindRefillToggleBlock,
  renderRefillToggleBlock,
} from "./quiz-results-refill-toggle.js";
import {
  bindAddonsBlock,
  getSortedAddons,
  renderAddonsBlock,
} from "./quiz-results-addons.js";
import {
  bindStickyBarBlock,
  getStickyTotals,
  renderStickyBarBlock,
  runStickyCheckoutFlow,
} from "./quiz-results-sticky-bar.js";
import {
  getDiscountForOtpState,
  resolveDiscountState,
} from "./quiz-results-discount.js";
import {
  initResultsTrackingConfig,
  trackAddonSelection,
  trackCheckout,
  trackDiscountView,
  trackResultsView,
} from "./quiz-results-tracking.js";

if (!customElements.get("quiz-results")) {
  customElements.define(
    "quiz-results",
    class QuizResults extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
        this.state = {
          status: "idle",
          results: null,
          savedSelection: {},
          productBlocks: [],
          regimenProduct: null,
          visibleAddons: [],
          recommendedAddons: [],
          isReady: false,
          discount: null,
          discountWithRefills: null,
          isOtpOn: false,
          discountMeta: {
            source: "",
          },
          blurb: {
            isLoading: true,
            text: "",
          },
          sticky: {
            isCheckoutLoading: false,
          },
        };

        // Keep reference to listener so we can remove it in disconnectedCallback.
        this.handleOctaneResults = this.handleOctaneResults.bind(this);
        this.unbindRefillToggle = null;
        this.unbindAddons = null;
        this.unbindStickyBar = null;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.init();
      }

      disconnectedCallback() {
        document.removeEventListener("octane.quiz.results", this.handleOctaneResults);
        if (this.unbindRefillToggle) {
          this.unbindRefillToggle();
          this.unbindRefillToggle = null;
        }
        if (this.unbindAddons) {
          this.unbindAddons();
          this.unbindAddons = null;
        }
        if (this.unbindStickyBar) {
          this.unbindStickyBar();
          this.unbindStickyBar = null;
        }
      }

      init() {
        initResultsTrackingConfig();

        /**
         * Initial data bootstrap:
         * 1) read localStorage payloads
         * 2) map Octane blocks to Shopify-compatible products
         * 3) derive main entities needed by downstream features
         */
        this.initializeDataState();

        /**
         * Keep compatibility with existing quiz flow.
         * Some environments republish Octane results without full page reload.
         */
        document.addEventListener("octane.quiz.results", this.handleOctaneResults);

        const regimenProduct = /** @type {any} */ (this.state.regimenProduct);
        console.log("quiz-results initialized", {
          status: this.state.status,
          hasResults: Boolean(this.state.results),
          regimenSku: regimenProduct?.variant?.sku || null,
          visibleAddonsCount: this.state.visibleAddons.length,
          recommendedAddonsCount: this.state.recommendedAddons.length,
        });
      }

      /**
       * Re-run data bootstrap when new Octane results are emitted.
       */
      handleOctaneResults() {
        this.initializeDataState();
      }

      /**
       * Resolve discount/coupon state from query, cookie, default config and API (if provided).
       * This mirrors Angular DiscountService initialization flow.
       */
      async initDiscountState() {
        const discountState = await resolveDiscountState({
          regimenProduct: this.state.regimenProduct,
        });

        const savedSelection = /** @type {any} */ (this.state.savedSelection);
        const savedOtp = Boolean(savedSelection?.is_otp_on);
        const activeDiscount = getDiscountForOtpState(
          savedOtp,
          discountState.discountWithRefills,
        );

        this.state = {
          ...this.state,
          isOtpOn: savedOtp,
          discount: activeDiscount,
          discountWithRefills: discountState.discountWithRefills,
          discountMeta: {
            source: discountState.source,
          },
        };

        this.emitDiscountChangedEvent();
        // trackDiscountView(this.state.discountWithRefills);
      }

      /**
       * Toggle between refill mode and one-time mode.
       * This updates active discount and persists selection.
       *
       * @param {boolean=} explicitValue
       */
      toggleOtp(explicitValue) {
        const nextOtpState =
          typeof explicitValue === "boolean" ? explicitValue : !this.state.isOtpOn;

        this.state = {
          ...this.state,
          isOtpOn: nextOtpState,
          discount: getDiscountForOtpState(
            nextOtpState,
            this.state.discountWithRefills,
          ),
        };

        this.persistSelection({
          is_otp_on: this.state.isOtpOn,
        });

        this.emitDiscountChangedEvent();
        this.renderRefillToggleBlock();
        this.renderRegimenPriceBlock();
        this.renderAddonsSection();
        this.renderStickyBarSection();
        this.renderOtpDependentSections();
      }

      /**
       * Persist partial selection updates while preserving existing values.
       *
       * @param {Record<string, any>} patch
       */
      persistSelection(patch) {
        const selection = {
          ...(this.state.savedSelection || {}),
          ...patch,
        };

        this.state = {
          ...this.state,
          savedSelection: selection,
        };

        localStorage.setItem("g_quiz_results_selection", JSON.stringify(selection));
      }

      emitDiscountChangedEvent() {
        const detail = {
          discount: this.state.discount,
          discountWithRefills: this.state.discountWithRefills,
          isOtpOn: this.state.isOtpOn,
          source: this.state.discountMeta?.source || null,
        };

        this.dispatchEvent(
          new CustomEvent("quiz-results:discount-changed", {
            bubbles: true,
            composed: true,
            detail,
          }),
        );
      }

      /**
       * Return selected addon variant IDs that are still visible in current results.
       *
       * @returns {number[]}
       */
      getSelectedAddonIds() {
        const savedSelection = /** @type {any} */ (this.state?.savedSelection || {});
        const selectedRaw = savedSelection.selected_addons;
        const selected = Array.isArray(selectedRaw) ? selectedRaw : [];
        const visibleAddons = /** @type {any[]} */ (this.state.visibleAddons || []);
        const visibleIds = new Set(
          visibleAddons
            .map((addon) => Number(addon?.variant?.id))
            .filter((id) => Number.isFinite(id)),
        );

        return selected
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && visibleIds.has(id));
      }

      /**
       * @param {number} variantId
       * @returns {boolean}
       */
      isAddonVisible(variantId) {
        const visibleAddons = /** @type {any[]} */ (this.state.visibleAddons || []);
        return visibleAddons.some(
          (addon) => Number(addon?.variant?.id) === Number(variantId),
        );
      }

      /**
       * @param {number} variantId
       * @returns {boolean}
       */
      isAddonSelected(variantId) {
        return this.getSelectedAddonIds().includes(Number(variantId));
      }

      /**
       * @param {number} variantId
       * @returns {any | undefined}
       */
      getAddonById(variantId) {
        const visibleAddons = /** @type {any[]} */ (this.state.visibleAddons || []);
        return visibleAddons.find(
          (addon) => Number(addon?.variant?.id) === Number(variantId),
        );
      }

      /**
       * @returns {any[]}
       */
      getSelectedAddons() {
        return this.getSelectedAddonIds()
          .map((variantId) => this.getAddonById(variantId))
          .filter(Boolean);
      }

      /**
       * Keep function parity with Angular AddonService.
       * The flow remains intentionally disabled until explicitly enabled.
       */
      autoSelectAddons() {
        const recommendedAddons = /** @type {any[]} */ (
          this.state.recommendedAddons || []
        );

        recommendedAddons.forEach((addon) => {
          const variantId = Number(addon?.variant?.id);
          if (!Number.isFinite(variantId)) return;
          if (!this.isAddonVisible(variantId)) return;

          this.toggleAddonSelection(variantId, true, true);
        });
      }

      /**
       * Keep function parity with Angular AddonService.
       * The flow remains intentionally disabled until explicitly enabled.
       *
       * @param {number[]} addonVariantIds
       */
      setFreeAddon(addonVariantIds) {
        if (!addonVariantIds.length) {
          this.persistSelection({ free_addon: null });
          return;
        }

        // const freeAddon = addonVariantIds.find((variantId) => this.isFree(variantId));
        // if (freeAddon) return;

        const sortedIds = [...addonVariantIds].sort((a, b) => {
          const addonA = this.getAddonById(a);
          const addonB = this.getAddonById(b);
          return Number(addonB?.variant?.price || 0) - Number(addonA?.variant?.price || 0);
        });

        this.persistSelection({ free_addon: sortedIds[0] });
      }

      /**
       * Toggle addon selection and persist to localStorage (Angular AddonService parity).
       *
       * @param {number} variantId
       * @param {boolean} isSelected
       * @param {boolean=} isAutoSelected
       */
      toggleAddonSelection(variantId, isSelected, isAutoSelected) {
        const current = new Set(this.getSelectedAddonIds());
        if (isSelected) {
          current.add(variantId);
        } else {
          current.delete(variantId);
        }

        // Keep parity with Angular signature while tracking is not migrated yet.
        const _isAutoSelected = isAutoSelected;
        void _isAutoSelected;

        const addonSelection = Array.from(current);
        // this.setFreeAddon(addonSelection);

        this.persistSelection({
          selected_addons: addonSelection,
        });

        this.renderAddonsSection();
        this.renderStickyBarSection();

        if (isSelected) {
          const addon = this.getAddonById(variantId);
          if (addon) {
            // trackAddonSelection({
            //   selectedAddons: [addon],
            //   isAutoSelected: Boolean(isAutoSelected),
            //   isOtpOn: this.state.isOtpOn,
            // });
          }
        }
      }

      /**
       * Build normalized in-memory data state.
       * This is the first migration step from Angular DataService.
       */
      async initializeDataState() {
        this.state = {
          ...this.state,
          status: "loading",
          isReady: false,
        };

        const dataState = buildInitialQuizDataState({});

        this.state = {
          ...this.state,
          ...dataState,
          status: dataState.results ? "ready" : "no-results",
          discount: null,
          discountWithRefills: null,
          isOtpOn: false,
          discountMeta: {
            source: "",
          },
          blurb: {
            isLoading: true,
            text: "",
          },
          sticky: {
            isCheckoutLoading: false,
          },
        };

        await this.initDiscountState();

        // Start section lifecycles after base data is prepared.
        this.initRefillToggleBlock();
        this.initRegimenSliderBlock();
        this.initAddonsSection();
        this.initStickyBarSection();
        this.initOtpDependentSections();
        this.initConcernsBlock();
        this.initBlurbBlock();

        // trackResultsView({
        //   regimenProduct: this.state.regimenProduct,
        //   discount: this.state.discount,
        // });

        /**
         * Emit custom event so future section renderers can react
         * without tightly coupling to this class internals.
         */
        const detail = this.state;

        // Keep last emitted payload for late subscribers.
        this.lastDataReadyDetail = detail;

        // Emit in a microtask so listeners attached right after define()
        // still receive the event.
        queueMicrotask(() => {
          const event = new CustomEvent("quiz-results:data-ready", {
            bubbles: true,
            composed: true,
            detail,
          });
          this.dispatchEvent(event);
          document.dispatchEvent(
            new CustomEvent("quiz-results:data-ready", { detail }),
          );
        });
      }

      /**
       * Render concerns list from quiz answers.
       * This migrates Angular regimen concerns logic into Shopify custom element flow.
       */
      initConcernsBlock() {
        const concernsContainer = this.querySelector("[data-concerns-container]");
        if (!concernsContainer) return;
        renderConcernsBlock(concernsContainer, this.state.results);
      }

      /**
       * Render regimen title + swiper slides in quiz-results-regimen-box section.
       */
      initRegimenSliderBlock() {
        const regimenSection =
          this.querySelector("[data-regimen-slider-section]") ||
          this.querySelector(".quiz-results-regimen-box");
        if (!regimenSection) return;
        renderRegimenSliderSection(regimenSection, this.state.regimenProduct);
        this.renderRegimenPriceBlock();
      }

      renderRegimenPriceBlock() {
        const regimenSection =
          this.querySelector("[data-regimen-slider-section]") ||
          this.querySelector(".quiz-results-regimen-box");
        if (!regimenSection) return;

        renderRegimenPriceLabel(
          regimenSection,
          this.state.regimenProduct,
          this.state.discount,
        );
      }

      /**
       * app-refill-toggle analog:
       * - binds radio controls to OTP state
       * - renders active option and helper text based on discount + OTP mode
       */
      initRefillToggleBlock() {
        const toggleRoot =
          this.querySelector("[data-refill-toggle]") ||
          this.querySelector(".quiz-results-toggle");
        if (!toggleRoot) return;

        // Avoid duplicate listeners when component re-initializes.
        if (!this.unbindRefillToggle) {
          this.unbindRefillToggle = bindRefillToggleBlock(
            toggleRoot,
            (isOtpOn) => this.toggleOtp(isOtpOn),
          );
        }

        this.renderRefillToggleBlock();
      }

      renderRefillToggleBlock() {
        const toggleRoot =
          this.querySelector("[data-refill-toggle]") ||
          this.querySelector(".quiz-results-toggle");
        if (!toggleRoot) return;

        renderRefillToggleBlock(toggleRoot, {
          discount: this.state.discount,
          isOtpOn: this.state.isOtpOn,
        });
      }

      /**
       * app-addons analog:
       * - sort addons with recommended first
       * - restore/persist selected addons
       * - update pricing and copy based on OTP mode
       */
      initAddonsSection() {
        const addonsRoot =
          this.querySelector("[data-addons-block]") ||
          this.querySelector(".quiz-results-addons");
        if (!addonsRoot) return;

        if (!this.getSelectedAddonIds().length) {
          // this.autoSelectAddons();
        }

        if (!this.unbindAddons) {
          this.unbindAddons = bindAddonsBlock(
            addonsRoot,
            (variantId, isSelected) => this.toggleAddonSelection(variantId, isSelected),
          );
        }

        this.renderAddonsSection();
      }

      renderAddonsSection() {
        const addonsRoot =
          this.querySelector("[data-addons-block]") ||
          this.querySelector(".quiz-results-addons");
        if (!addonsRoot) return;

        const sortedAddons = getSortedAddons(
          this.state.visibleAddons,
          this.state.recommendedAddons,
        );
        const discount = /** @type {any} */ (this.state.discount);
        const trialDiscountValue = Number(discount?.value || 0);

        renderAddonsBlock(addonsRoot, {
          addons: sortedAddons,
          selectedAddonIds: this.getSelectedAddonIds(),
          isOtpOn: this.state.isOtpOn,
          trialDiscountValue,
        });
      }

      initStickyBarSection() {
        const stickyRoot =
          this.querySelector("[data-sticky-bar]") ||
          this.querySelector(".quiz-results-sticky-bar");
        if (!stickyRoot) return;

        if (!this.unbindStickyBar) {
          this.unbindStickyBar = bindStickyBarBlock(stickyRoot, () =>
            this.handleStickyCheckout(),
          );
        }

        this.renderStickyBarSection();
      }

      renderStickyBarSection() {
        const stickyRoot =
          this.querySelector("[data-sticky-bar]") ||
          this.querySelector(".quiz-results-sticky-bar");
        if (!stickyRoot) return;

        const totals = getStickyTotals(
          this.state.regimenProduct,
          this.getSelectedAddons(),
          this.state.discount,
          this.state.isOtpOn,
        );

        renderStickyBarBlock(stickyRoot, {
          fullTotal: totals.fullTotal,
          discountedTotal: totals.discountedTotal,
          isLoading: Boolean(this.state?.sticky?.isCheckoutLoading),
        });
      }

      /**
       * Show how-it-works and benefit sections only for subscription mode.
       * OTP ON (one-time purchase) => hide both sections.
       */
      initOtpDependentSections() {
        this.renderOtpDependentSections();
      }

      renderOtpDependentSections() {
        const shouldHide = Boolean(this.state.isOtpOn);
        const howItWorksSection = this.querySelector("[data-quiz-results-how-it-works]");
        const benefitSection = this.querySelector("[data-quiz-results-benefit]");

        if (howItWorksSection) {
          howItWorksSection.classList.toggle("is-hidden", shouldHide);
        }

        if (benefitSection) {
          benefitSection.classList.toggle("is-hidden", shouldHide);
        }
      }

      async handleStickyCheckout() {
        if (this.state?.sticky?.isCheckoutLoading) return;
        if (!this.state.regimenProduct) return;

        this.state = {
          ...this.state,
          sticky: {
            ...(this.state.sticky || {}),
            isCheckoutLoading: true,
          },
        };
        this.renderStickyBarSection();

        try {
          const results = /** @type {any} */ (this.state.results);
          // trackCheckout({
          //   regimenProduct: this.state.regimenProduct,
          //   selectedAddons: this.getSelectedAddons(),
          //   discount: this.state.discount,
          //   isOtpOn: this.state.isOtpOn,
          // });

          await runStickyCheckoutFlow({
            regimenProduct: this.state.regimenProduct,
            selectedAddons: this.getSelectedAddons(),
            discount: this.state.discount,
            isOtpOn: this.state.isOtpOn,
            email: String(results?.email || ""),
          });
        } catch (_error) {
          this.state = {
            ...this.state,
            sticky: {
              ...(this.state.sticky || {}),
              isCheckoutLoading: false,
            },
          };
          this.renderStickyBarSection();
        }
      }

      /**
       * app-blurb analog:
       * 1) render loading state
       * 2) request blurb text
       * 3) render resulting text state
       */
      async initBlurbBlock() {
        const blurbContainer = this.querySelector("[data-blurb-container]");
        if (!blurbContainer) return;

        renderBlurbBlock(blurbContainer, { isLoading: true, text: "" });

        if (!this.state.results || !this.state.regimenProduct) {
          this.state = {
            ...this.state,
            blurb: {
              isLoading: false,
              text: "",
            },
          };
          renderBlurbBlock(blurbContainer, this.state.blurb);
          return;
        }

        const text = await fetchBlurbText({
          results: this.state.results,
          regimenProduct: this.state.regimenProduct,
        });

        this.state = {
          ...this.state,
          blurb: {
            isLoading: false,
            text,
          },
        };

        renderBlurbBlock(blurbContainer, this.state.blurb);
      }
    },
  );
}


document.addEventListener("quiz-results:data-ready", (event) => {
  const customEvent = /** @type {CustomEvent} */ (event);
  console.log("quiz-results:data-ready detail:", customEvent.detail);
});